/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, IconButton, makeStyles, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import CloseIcon from '@material-ui/icons/Close';
import { formatNumber, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import { SelectedItemProps } from '../MattersByStatusChart';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import HourglassEmptyOutlinedIcon from '@material-ui/icons/HourglassEmptyOutlined';
import { RV_CHART_CLASS, RV_CHART_COLORS, rvBarDatasetStyle, rvChartAnimation, rvValueAxisStyle, rvCategoryAxisStyle, rvTooltipStyle, isChartDataEmpty } from '../chartTheme';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartHeading } from '../ChartHeading';
import { ChartNotes } from '../ChartNotes';
import { AnimatedNumber } from '../AnimatedNumber';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick } from 'chart.js';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import MTooltip from '@material-ui/core/Tooltip';
import { ChartInfo } from '../../components/ChartInfo';
import { VideoModal } from '../../components/VideoModal';
import { AIIcon } from '../../components/icons/AIIcon';
import html2canvas from 'html2canvas';
import { AIChartDialog } from '../../components/Dialogs/AIChartDialog';
import { CurrencySymbolData, CURRENCY_SYMBOL_QUERY } from '../AgedUnbilledFees/AgedUnbilledFeesChartV2';
import { DrillDownType } from '../DrillDownType';
import { SearchCriteria } from '../SearchCriteria';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const PERIOD_LABELS = ['Current', '30 days', '60 days', '90 days'];

const PERIOD_COLOURS = [RV_CHART_COLORS.primarySoft, RV_CHART_COLORS.primary, RV_CHART_COLORS.amber, RV_CHART_COLORS.red];

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px 15px',
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '350px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        span: {
            fontSize: '1rem',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)',
        },
    }),
);

interface StaffUnbilledFeesByPeriodChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

export const StaffUnbilledFeesByPeriodChart: React.FC<StaffUnbilledFeesByPeriodChartProps> = (props) => {
    const classes = useStyles();
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    const [base64Data, setBase64Data] = useState<string | ArrayBuffer | null>(null);

    const [isAIDialogOpen, setIsAIDialogOpen] = useState<boolean>(false);

    const [isBlobGenerating, setIsBlobGenerating] = useState<boolean>(false);

    const [currencySymbol, setCurrencySymbol] = useState<string>('$');

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const chartName = 'Unbilled Fees By Fee Earner by Period';

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    const staffUnbilledFeesByPeriodQuery = useQuery<StaffUnbilledFeesByPeriodData>(StaffUnbilledFeesByPeriodQuery, {
        fetchPolicy: 'network-only',
        variables : {
            entityGuid: props.chartFilter && props.chartFilter.entityGuid !== undefined ? props.chartFilter.entityGuid : null,
            entityType: props.chartFilter && props.chartFilter.entityType !== undefined ? props.chartFilter.entityType : null,
            responsibility: props.chartFilter && props.chartFilter.responsibility !== undefined ? props.chartFilter.responsibility : null,
            include: null,
        }
    });

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setCurrencySymbol(currencySymbolQuery.data.options.currencySymbol.symbol);
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    const staffUnbilledFeesByPeriod = staffUnbilledFeesByPeriodQuery.data?.statistics?.workInProgress?.staffUnbilledFeesByPeriod;

    const periodValues = [
        staffUnbilledFeesByPeriod?.current ?? 0,
        staffUnbilledFeesByPeriod?.last30Days ?? 0,
        staffUnbilledFeesByPeriod?.last60Days ?? 0,
        staffUnbilledFeesByPeriod?.last90Days ?? 0,
    ];

    const totalUnbilledFees = staffUnbilledFeesByPeriod?.total ?? 0;

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart(`isStaffUnbilledFeesByPeriodChartOpen`);
        }
    };

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    // tslint:disable-next-line: no-any
    const onDownload = (name: string, extension: string = 'jpg') => (image: any) => {
        const anchor = document.createElement('a');
        anchor.href = image;
        anchor.download = createFileName(extension, name);
        anchor.click();
    };

    const onDownloadScreenshot = () => {
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload(chartName));
            // tslint:disable-next-line: align
        }, 500);
    };

    // tslint:disable-next-line: no-any
    const onAIGeneratedSuggestion = async (event: any) => {
        setIsBlobGenerating(true);

        event.preventDefault();
        event.stopPropagation();

        if (entireChartRef && entireChartRef.current) {
            const canvas = await html2canvas(entireChartRef.current);
            canvas.toBlob((blob) => {
                if (blob) {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = async () => {
                        setBase64Data(reader.result);
                        setIsAIDialogOpen(true);
                    };
                }
            });
        }

        setIsBlobGenerating(false);
    };

    const chartControlComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <div className={`${classes.helpIcon} rv-chart-help`}>
                        <ChartInfo description={props.description} fontSize={props.showAllRecords ? 'large' : 'default'} />
                        {props.infoUrl && (
                            <MTooltip title="Learn more">
                                <IconButton className={classes.learnIcon} aria-label="Learn more" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                    <VideoLibraryOutlinedIcon />
                                </IconButton>
                            </MTooltip>
                        )}
                    </div>
                    {props.isAIFeatureEnabled && (
                        <MTooltip title="AI Assistant">
                            <IconButton disabled={isBlobGenerating} size={props.showAllRecords ? 'medium' : 'small'} onClick={onAIGeneratedSuggestion}>
                                <AIIcon color="primary" fontSize={props.showAllRecords ? 'large' : 'default'} />
                            </IconButton>
                        </MTooltip>
                    )}
                    {props.showAllRecords && (
                        <IconButton size="medium" onClick={onDownloadScreenshot}>
                            <GetAppOutlinedIcon color="primary" fontSize="large" />
                        </IconButton>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon className={classes.closeIcon} fontSize={props.showAllRecords ? 'large' : 'default'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    const handleTotalClick = () => {
        if (props.getSelectedItem) {

            const searchCriterias = new Array<SearchCriteria>();

            props.getSelectedItem(
                searchCriterias,
                false,
                `Total ${chartName}`,
                DrillDownType.unbilledFeeEarnerDrilldown,
                'Unbilled Fees'
            );
        }        
    };

    if (staffUnbilledFeesByPeriodQuery.loading && !staffUnbilledFeesByPeriodQuery.data) {
        return (
            <div className={`${classes.loadingWrapper} ${RV_CHART_CLASS}`}>
                {chartControlComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    return (
        <>
            {isAIDialogOpen && (
                <AIChartDialog
                    fullWidth={true}
                    maxWidth="md"
                    open={isAIDialogOpen}
                    onClose={() => setIsAIDialogOpen(false)}
                    data={base64Data}
                    query={props.aiPrompt || 'Give me recommendations on this and highlight any exceptions or concerns'}
                    accessSubType={chartName}
                    layoutId={props.layoutId}
                />
            )}
            <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(periodValues) ? ' rv-chart--empty' : ''}`} ref={entireChartRef}>
                {isVideoModalOpen && props.infoUrl && <VideoModal open={isVideoModalOpen} onClose={onToggleVideo} url={props.infoUrl} />}
                {chartControlComponent()}
                <div className={classes.titleContainer}>
                    <ChartHeading icon={<HourglassEmptyOutlinedIcon />} title={chartName} />
                    <div className={`${classes.total} h2`} onClick={handleTotalClick}>
                        <AnimatedNumber value={totalUnbilledFees} format={(amount) => `${currencySymbol}${formatNumber(amount)}`} />
                    </div>
                </div>
                <div className={classes.barContainer}>
                    <Bar
                        ref={chartRef}
                        // tslint:disable-next-line: no-any
                        onClick={(event: React.MouseEvent<any>) => {
                            const clickedElements = getElementAtEvent(chartRef.current, event);
                            if (clickedElements.length > 0 && props.getSelectedItem) {
                                const clickedPeriodLabel = PERIOD_LABELS[clickedElements[0].index];

                                const searchCriterias = new Array<SearchCriteria>();

                                searchCriterias.push({ property: 'Period', operator: 'eq', comparison: clickedPeriodLabel === 'Current' ? clickedPeriodLabel : 'Last' + clickedPeriodLabel.replaceAll(' ', '') });

                                props.getSelectedItem(
                                    searchCriterias,
                                    true,
                                    `${chartName} - ${clickedPeriodLabel}`,
                                    DrillDownType.unbilledFeeEarnerDrilldown,
                                    'Unbilled Fees',
                                );
                            }
                        }}
                        data={{
                            labels: PERIOD_LABELS,
                            datasets: [
                                {
                                    label: 'Unbilled Fees',
                                    ...rvBarDatasetStyle(PERIOD_COLOURS),
                                    data: periodValues,
                                },
                            ],
                        }}
                        options={{
                            indexAxis: 'y' as const,
                            animation: rvChartAnimation(),
                            elements: { bar: { borderWidth: 0 } },
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                                x: {
                                    grid: rvValueAxisStyle.grid,
                                    suggestedMax: getSuggestedMaxTick(periodValues),
                                    beginAtZero: true,
                                    ticks: {
                                        ...rvValueAxisStyle.ticks,
                                        maxTicksLimit: getMaxTickLimit(periodValues),
                                        autoSkip: false,
                                        callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                            return `${currencySymbol}${formatNumber(tickValue)}`;
                                        },
                                    },
                                },
                                y: {
                                    grid: rvCategoryAxisStyle.grid,
                                    ticks: {
                                        ...rvCategoryAxisStyle.ticks,
                                        autoSkip: false,
                                    },
                                },
                            },
                            plugins: {
                                legend: {
                                    display: false,
                                },
                                title: {
                                    display: false,
                                    text: ``,
                                },
                                tooltip: {
                                    ...rvTooltipStyle,
                                    callbacks: {
                                        // tslint:disable-next-line: no-any
                                        label: function (context: any) {
                                            return `${currencySymbol}${formatNumber(periodValues[context.dataIndex])}`;
                                        },
                                    },
                                },
                            },
                        }}
                    />
                    {isChartDataEmpty(periodValues) && <ChartEmptyState title="No unbilled fees" hint="Nothing is unbilled right now." />}
                </div>
                <ChartNotes notes={['Fees only, disbursement are not included', 'Information collated by Fee Earner']} />
            </div>
        </>
    );
};

export interface StaffUnbilledFeesByPeriodData {
    statistics: Statistics;
}

export interface Statistics {
    workInProgress: WorkInProgress;
}

export interface WorkInProgress {
    staffUnbilledFeesByPeriod: StaffUnbilledFeesByPeriod;
}

export interface StaffUnbilledFeesByPeriod {
    total: number;
    current: number;
    last30Days: number;
    last60Days: number;
    last90Days: number;
}

const StaffUnbilledFeesByPeriodQuery = gql`
    query UnbilledFeesByFeeEarnerByPeriod ($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            workInProgress {
                staffUnbilledFeesByPeriod (entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    total
                    current
                    last30Days
                    last60Days
                    last90Days
                }
            }
        }
    }
`;
